import {
  SanityPrintType,
  SanityProductPrintConfiguration,
} from '@data/sanity/queries/types/print'
import { SanityProductGalleryPhoto } from '@data/sanity/queries/types/product'
import { SanityPrintConfiguratorStrings } from '@data/sanity/queries/types/strings'
import { RadioItem } from '@lib/radio'
import {
  ImagePrintConfiguration,
  ImageWithAreas,
  ProductGalleryImageWithAreas,
  UploadAction,
  UploadState,
} from './types'

/**
 * Gets product gallery images with added slot types and print areas.
 */
export const getProductGalleryWithPrintAreas = (
  galleryPhotos?: SanityProductGalleryPhoto[],
  printConfiguration?: SanityProductPrintConfiguration[],
) => {
  if (!galleryPhotos) {
    return []
  }

  // Get master configuration items for each slot
  const masterPrintConfiguration = printConfiguration?.map(
    (printConfigurationItem) => {
      const colorConfigurationItem =
        printConfigurationItem.colorConfiguration.find(
          (colorConfigurationItem) => colorConfigurationItem.isMaster,
        )
      const imagePrintConfiguration: ImagePrintConfiguration = {
        slotType: printConfigurationItem.slot.type,
        areas: colorConfigurationItem?.printAreas,
      }

      return imagePrintConfiguration
    },
  )

  return galleryPhotos.map((galleryPhoto) => {
    const imageWithAreas: ProductGalleryImageWithAreas = {
      forOption: galleryPhoto.forOption,
      imagesWithAreas: galleryPhoto.photos.map((photo) => {
        const imageConfigurations =
          printConfiguration
            ?.map((printConfigurationItem) => {
              const colorConfiguration =
                printConfigurationItem.colorConfiguration.find(
                  (colorConfigurationItem) => {
                    return (
                      colorConfigurationItem.galleryItem ===
                        galleryPhoto.forOption &&
                      colorConfigurationItem.galleryPhoto === photo.asset?._ref
                    )
                  },
                )
              const masterConfiguration = masterPrintConfiguration?.find(
                (masterPrintConfigurationItem) => {
                  return (
                    masterPrintConfigurationItem.slotType ===
                    printConfigurationItem.slot.type
                  )
                },
              )
              const imagePrintConfiguration: ImagePrintConfiguration = {
                slotType: printConfigurationItem.slot.type,
                areas: colorConfiguration?.isInheritedFromMaster
                  ? masterConfiguration?.areas
                  : colorConfiguration?.printAreas,
              }

              return imagePrintConfiguration
            })
            .filter((imageConfiguration) => !!imageConfiguration.areas) ?? []

        const imageWithAreas: ImageWithAreas = {
          ...(imageConfigurations[0] ?? {}),
          image: photo,
        }

        return imageWithAreas
      }),
    }

    return imageWithAreas
  })
}

/**
 * Gets print image type radio items.
 */
export const getImageTypeRadioItems = (
  getFormattedPrice: (minorUnits: number) => string,
  printConfiguratorStrings: SanityPrintConfiguratorStrings,
  hasLogo?: boolean,
  hasImage?: boolean,
  logoPrintFeePrice?: number,
  imagePrintFeePrice?: number,
) => {
  const radioItems: RadioItem<SanityPrintType>[] = []

  if (hasLogo) {
    radioItems.push({
      value: 'logo',
      label: `${printConfiguratorStrings.printConfiguratorLogoLabel}${
        typeof logoPrintFeePrice !== 'undefined'
          ? ` (+${getFormattedPrice(logoPrintFeePrice)})`
          : ''
      }`,
    })
  }

  if (hasImage) {
    radioItems.push({
      value: 'image',
      label: `${printConfiguratorStrings.printConfiguratorImageLabel}${
        typeof imagePrintFeePrice !== 'undefined'
          ? ` (+${getFormattedPrice(imagePrintFeePrice)})`
          : ''
      }`,
    })
  }

  return radioItems
}

/**
 * Updates upload state using an upload action.
 */
export const uploadStateReducer = (
  state: UploadState,
  action: UploadAction,
): UploadState => {
  switch (action.type) {
    case 'updateDropDepth': {
      const dropDepth = Math.max(0, state.dropDepth + action.dropDepthDelta)

      return {
        ...state,
        dropDepth,
        ...(dropDepth === 0
          ? {
              isInDropZone: false,
            }
          : {}),
      }
    }

    case 'setIsInDropZone': {
      return {
        ...state,
        isInDropZone: action.isInDropZone,
      }
    }
  }
}
